/* eslint-disable */
import { withStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { ORDER_API_DATE_FORMAT } from '../../Constants/Order/Order.constants';
import { createOrderPageLink } from '../../Pages/Order/Order.page.route';
import ordersActionCreators from '../../State/Store/Orders/Orders.actions';
import orderItemSelector from '../../State/Store/Orders/Selectors/OrderItem.selector';
import ordersSelectedSelector from '../../State/Store/Orders/Selectors/OrderSelected.selector';
import uiActionCreators from '../../State/Store/UI/UI.actions';
import { UI_ORDER_PAGE_ORDER_CLICKED } from '../../State/Store/UI/UI.types';
import dateOnly from '../../Support/DateTime/DateOnly/DateTimeDateOnly.support';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import OrderSummary from '../OrderSummary/OrderSummary.component';
import OrderCardPropTypes from './OrderCard.component.props';
import orderCardStyles from './OrderCard.component.styles';

const parseInteger = (i) => {
  if (i > 0) {
    return `+${i}`;
  }
  return `${i}`;
};

/**
 * OrderCardComponent
 * @export
 * @class OrderCardComponent
 * @extends {React.Component<OrderCardComponentComposedProps, OrderCardComponentState>}
 */
class OrderCardComponent extends React.Component {
  orderCardRef = React.createRef();

  /**
   * The `props` to seed when any specific prop receives an `undefined` value. NOT a `null` value
   * @static
   * @type {Partial<OrderCardComponentProps>}
   * @memberof OrderCardComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`OrderCard`}
   * @memberof OrderCardComponent
   */
  static displayName = `OrderCard`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof OrderCardComponent
   */
  static propTypes = OrderCardPropTypes;

  /**
   * isOrderIdInUrl
   * @returns {boolean} if order id is in url hash
   * @memberof OrderCardComponent
   */
  isOrderIdInUrl = () => {
    const { orderId } = this.props;
    return window.location.hash === `#${orderId}`;
  };

  /**
   * componentDidMount
   * @returns {void} void
   * @memberof OrderCardComponent
   */
  componentDidMount() {
    if (this.isSelected() && this.orderCardRef.current) {
      const { actions } = this.props;
      actions.orderIdUnselected();
      this.orderCardRef.current.scrollIntoView(false);
    }
    return;
  }

  /**
   * On order card click
   * @returns {void} void
   * @memberof OrderCardComponent
   */
  onClick = () => {
    const { actions, history, orderId } = this.props;
    actions.uiTriggered({
      payload: {
        data: {
          name: `orderPageOrderClicked`,
        },
      },
      type: UI_ORDER_PAGE_ORDER_CLICKED,
    });
    history.push(createOrderPageLink({ orderId }));
    return;
  };

  /**
   * Determines if the current order card is selected
   * @returns {boolean} Is selected order card
   * @memberof OrderCardComponent
   */
  isSelected = () => {
    const { orderId, selectedOrderId } = this.props;
    return orderId === selectedOrderId;
  };

  /**
   * Render the component
   * @returns {React.Component} The rendered `<OrderCardRawComponent />`
   * @memberof OrderCardRawComponent
   */
  render() {
    const { classes, currentOrder, orderId } = this.props;
    if (!currentOrder) {
      return null;
    }
    const { summary } = currentOrder;
    if (!summary) {
      return null;
    }
    const { startTime } = summary;
    // "replace" below is to take care of invalid date format being send from backend
    const orderDate = moment(dateOnly(startTime.replace(`.`, `:`), ORDER_API_DATE_FORMAT));
    const dayDiff = orderDate.diff(moment().format(ORDER_API_DATE_FORMAT), `days`);
    const qaDayAttribute = dayDiff === 0 ? `today0` : `today${parseInteger(dayDiff)}`;
    /* eslint-disable jsx-a11y/interactive-supports-focus */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div
        data-qa-day={qaDayAttribute}
        className={cn(this.isOrderIdInUrl() && classes.highlight, classes.orderCardContainer)}
        id={`orderCard-${orderId}`}
        onClick={this.onClick}
        ref={this.orderCardRef}
        role="button"
      >
        <OrderSummary summary={summary} qaDayAttribute={qaDayAttribute} />
      </div>
    );
  }
}

/** @type {React.ComponentClass<OrderCardComponentProps>} */
const OrderCard = compose(
  withRouter,
  withStyles(themeCreateInjectableStyles(orderCardStyles), { withTheme: true }),
  connect(
    () => (state, ownProps) => {
      const { orderId } = ownProps;
      const orderEntitySelector = orderItemSelector(orderId);
      return {
        currentOrder: orderEntitySelector(state),
        selectedOrderId: ordersSelectedSelector(state),
      };
    },
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...ordersActionCreators,
          ...uiActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(OrderCardComponent);

export default OrderCard;
